import Delete from '@mui/icons-material/Delete'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridValueFormatterParams
} from '@mui/x-data-grid-pro'
import { FC, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetOtherPeopleProfilesQuery } from '../../redux/api'
import { useGetUpcomingUnarchivedAutomatedVehicleReminderQuery } from '../../redux/api/vehicleApi'
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter/capitalizeFirstLetter'
import formatMdyDate from '../../utils/formatMdyDate/formatMdyDate'
import { generateRruleProse } from '../../utils/generateRrule/generateRrule'
import { AutomatedSendReminderDeleteDialog } from './AutomatedSendReminderDeleteDialog'

interface Props {
  open: boolean
  handleClose: () => void
  handleCreate: () => void
}

export const AutomatedSendReminderMainDialog: FC<Props> = ({
  open,
  handleClose,
  handleCreate,
  ...props
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()

  const { data: automatedVehicleReminderEntries } =
    useGetUpcomingUnarchivedAutomatedVehicleReminderQuery({
      authorization: accessToken,
      shop: shopId
    })

  const { data: creators } = useGetOtherPeopleProfilesQuery(
    {
      authorization: accessToken,
      user: automatedVehicleReminderEntries?.map((avr) => avr.createdBy!) || []
    },
    { skip: !automatedVehicleReminderEntries }
  )

  const [deleteId, setDeleteId] = useState<string | undefined>(undefined)

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'type',
        headerName: 'Type',
        flex: 1
      },
      {
        field: 'timeframe',
        headerName: 'Timeframe',
        flex: 1
      },
      {
        field: 'rrule',
        headerName: 'Frequency',
        flex: 3
      },
      {
        field: 'rdate',
        headerName: 'Next Send',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value ? formatMdyDate(params.value as Date) : ''
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value ? formatMdyDate(params.value as Date) : ''
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 1
      },
      {
        field: 'actions',
        type: 'actions',
        width: 50,
        getActions: (params: GridRenderCellParams) => {
          return [
            <GridActionsCellItem
              icon={<Delete />}
              label="Delete"
              key="Delete"
              onClick={() => {
                setDeleteId(params.id as string)
              }}
            />
          ]
        }
      }
    ]
  }, [])

  const rows: GridRowsProp = useMemo(() => {
    return (automatedVehicleReminderEntries || []).map((avr) => ({
      id: avr.id,
      createdAt: avr.createdAt,
      createdBy: creators?.find((creator) => creator.id === avr.createdBy)
        ?.name,
      type: avr.info?.type
        ? capitalizeFirstLetter(avr.info?.type.toString().replaceAll(/_/g, ' '))
        : '',
      timeframe: avr.info?.timeframe
        ? avr.info?.timeframe.toString().replaceAll(/_/g, ' ')
        : '',
      rdate: avr.info?.rrule?.rdate,
      rrule: avr.info?.rrule ? generateRruleProse(avr.info?.rrule) : ''
    }))
  }, [automatedVehicleReminderEntries, creators])

  return (
    <>
      {automatedVehicleReminderEntries && deleteId && (
        <AutomatedSendReminderDeleteDialog
          entry={
            automatedVehicleReminderEntries.find((avr) => avr.id === deleteId)!
          }
          handleClose={() => {
            setDeleteId(undefined)
          }}
          handleSubmit={() => {
            setDeleteId(undefined)
          }}
        />
      )}

      <Dialog
        {...props}
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Schedule Reminder</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            pb={1}>
            <Typography>View all scheduled vehicle reminders</Typography>
            <Button variant="outlined" onClick={handleCreate}>
              + Create
            </Button>
          </Stack>

          <DataGridPro
            density="compact"
            rows={rows}
            columns={columns}
            autoHeight
            hideFooter
          />
        </DialogContent>

        <DialogActions sx={{ pt: 2 }}>
          <Button variant="text" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
